import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import MoleculeLoader from '../components/MoleculeLoader';
import { ExternalLink, BookOpen, FlaskConical, ShoppingBag, Link2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_ICONS = {
  'Guides': BookOpen,
  'Protocols': FlaskConical,
  'Supplies': ShoppingBag,
  'General': Link2,
};

const CATEGORY_COLORS = {
  'Guides': 'bg-blue-50 text-blue-700 border-blue-200',
  'Protocols': 'bg-purple-50 text-purple-700 border-purple-200',
  'Supplies': 'bg-amber-50 text-amber-700 border-amber-200',
  'General': 'bg-slate-50 text-slate-700 border-slate-200',
};

export default function ResourcesPage() {
  const { user, token } = useAuth();
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('All');

  useEffect(() => {
    fetchResources();
  }, []);

  const fetchResources = async () => {
    try {
      const res = await fetch(`${API_URL}/api/resources`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      if (res.ok) {
        const data = await res.json();
        setResources(data);
      }
    } catch (err) {
      console.error('Failed to fetch resources:', err);
    } finally {
      setLoading(false);
    }
  };

  const categories = ['All', ...new Set(resources.map(r => r.category))];
  const filtered = activeCategory === 'All' ? resources : resources.filter(r => r.category === activeCategory);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <MoleculeLoader size={72} label="Loading resources…" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="resources-page">
      {/* Hero */}
      <div className="bg-gradient-to-b from-slate-900 to-slate-800 text-white py-12 px-4">
        <div className="max-w-5xl mx-auto">
          <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-teal-500/20 text-teal-300 border border-teal-500/30 mb-4">
            Members Only
          </span>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Resources</h1>
          <p className="text-slate-400 mt-2 text-base">Guides, protocols, and helpful links for your research.</p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Category filter */}
        {categories.length > 2 && (
          <div className="flex flex-wrap gap-2 mb-8" data-testid="resources-category-filter">
            {categories.map(cat => {
              const Icon = CATEGORY_ICONS[cat] || Link2;
              return (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-sm font-medium transition-all border ${
                    activeCategory === cat
                      ? 'bg-teal-700 text-white border-teal-700'
                      : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                  }`}
                  data-testid={`category-filter-${cat.toLowerCase()}`}
                >
                  {cat !== 'All' && <Icon className="w-3.5 h-3.5" />}
                  {cat}
                </button>
              );
            })}
          </div>
        )}

        {filtered.length === 0 ? (
          <div className="text-center py-16 text-slate-400">
            <BookOpen className="w-12 h-12 mx-auto mb-3 opacity-40" />
            <p className="text-lg font-medium">No resources yet</p>
            <p className="text-sm mt-1">Check back soon for helpful guides and links.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="resources-grid">
            {filtered.map(resource => {
              const catColor = CATEGORY_COLORS[resource.category] || CATEGORY_COLORS['General'];
              const Icon = CATEGORY_ICONS[resource.category] || Link2;
              return (
                <a
                  key={resource.resource_id}
                  href={resource.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group block rounded-xl border border-slate-200 bg-white overflow-hidden hover:shadow-lg hover:border-slate-300 transition-all duration-200"
                  data-testid={`resource-card-${resource.resource_id}`}
                >
                  {/* Thumbnail */}
                  <div className="aspect-video bg-slate-100 relative overflow-hidden">
                    {resource.thumbnail_url ? (
                      <img
                        src={`${API_URL}${resource.thumbnail_url}`}
                        alt={resource.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200">
                        <Icon className="w-10 h-10 text-slate-300" />
                      </div>
                    )}
                    <span className={`absolute top-2.5 left-2.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold border ${catColor}`}>
                      <Icon className="w-3 h-3" />
                      {resource.category}
                    </span>
                  </div>
                  {/* Content */}
                  <div className="p-4">
                    <h3 className="font-semibold text-slate-900 group-hover:text-teal-700 transition-colors line-clamp-2 text-sm leading-snug">
                      {resource.title}
                    </h3>
                    {resource.description && (
                      <p className="text-xs text-slate-500 mt-1.5 line-clamp-2">{resource.description}</p>
                    )}
                    <div className="flex items-center gap-1 mt-3 text-xs text-teal-600 font-medium">
                      <ExternalLink className="w-3 h-3" />
                      Open link
                    </div>
                  </div>
                </a>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
